import { defineStore } from "pinia";
import { ref, computed } from "vue";

interface User {
  id: string;
  email: string;
  name: string | null;
  role: string;
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
}

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
  tenant: Tenant;
}

function safeJsonParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

const STORAGE_KEYS = {
  ACCESS: "lf_access_token",
  REFRESH: "lf_refresh_token",
  USER: "lf_user",
  TENANT: "lf_tenant",
};

export const useAuthStore = defineStore("auth", () => {
  const accessToken = ref<string>(
    localStorage.getItem(STORAGE_KEYS.ACCESS) || "",
  );
  const refreshToken = ref<string>(
    localStorage.getItem(STORAGE_KEYS.REFRESH) || "",
  );
  const user = ref<User | null>(
    safeJsonParse<User>(localStorage.getItem(STORAGE_KEYS.USER)),
  );
  const tenant = ref<Tenant | null>(
    safeJsonParse<Tenant>(localStorage.getItem(STORAGE_KEYS.TENANT)),
  );

  const isLoggedIn = computed(() => !!accessToken.value);

  async function login(tenantSlug: string, email: string, password: string) {
    const { api } = await import("@/api/client");
    const res = await api.post<LoginResponse>("/auth/login", {
      tenantSlug,
      email,
      password,
    });
    accessToken.value = res.data.accessToken;
    refreshToken.value = res.data.refreshToken;
    user.value = res.data.user;
    tenant.value = res.data.tenant;
    localStorage.setItem(STORAGE_KEYS.ACCESS, accessToken.value);
    localStorage.setItem(STORAGE_KEYS.REFRESH, refreshToken.value);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user.value));
    localStorage.setItem(STORAGE_KEYS.TENANT, JSON.stringify(tenant.value));
  }

  function logout() {
    accessToken.value = "";
    refreshToken.value = "";
    user.value = null;
    tenant.value = null;
    Object.values(STORAGE_KEYS).forEach((k) => localStorage.removeItem(k));
  }

  return { accessToken, user, tenant, isLoggedIn, login, logout };
});
